import { useState, type FormEvent } from 'react';
import { Logo } from '../components/Logo';
import { audio } from '../game/audio/audio';
import { api, ApiError } from '../services/api';
import { firebaseSendPasswordReset, firebaseSignIn, firebaseSignUp } from '../services/firebase-auth';
import { useSession } from '../stores/session-store';

export function AuthScreen() {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setFields({});
    if (mode === 'register' && password !== confirm) {
      setFields({ confirm: 'Passwords do not match.' });
      return;
    }
    setBusy(true);
    try {
      // One account for both games: sign in with Firebase, then exchange the ID token for a
      // game session on this server.
      const idToken =
        mode === 'login' ? await firebaseSignIn(email, password) : await firebaseSignUp(email, password);
      const r = await api.firebaseLogin(idToken);
      audio.play('ui_confirm');
      useSession.getState().setUser(r.user);
      useSession.getState().go('menu');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else setError('Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const [resetNote, setResetNote] = useState<string | null>(null);
  const forgotPassword = async (): Promise<void> => {
    setError(null);
    setResetNote(null);
    if (!email) {
      setFields({ email: 'Enter your email first.' });
      return;
    }
    try {
      await firebaseSendPasswordReset(email);
      setResetNote('Password reset email sent — check your inbox.');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else setError('Something went wrong.');
    }
  };

  return (
    <main className="screen">
      <div className="col" style={{ alignItems: 'center', width: '100%' }}>
        <Logo />
        <form
          className="panel auth-card"
          onSubmit={submit}
          noValidate
          aria-label={mode === 'login' ? 'Log in' : 'Create account'}
        >
          <div className="tabs" role="tablist">
            <button
              type="button"
              role="tab"
              className="tab"
              aria-selected={mode === 'login'}
              onClick={() => setMode('login')}
            >
              Log In
            </button>
            <button
              type="button"
              role="tab"
              className="tab"
              aria-selected={mode === 'register'}
              onClick={() => setMode('register')}
            >
              Create Account
            </button>
          </div>
          {error && (
            <div className="form-error" role="alert">
              {error}
            </div>
          )}
          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              className={`input ${fields.email ? 'invalid' : ''}`}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {fields.email && <span className="field-error">{fields.email}</span>}
          </div>
          <div className="field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              className={`input ${fields.password ? 'invalid' : ''}`}
              type="password"
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={mode === 'register' ? 6 : 1}
            />
            {fields.password && <span className="field-error">{fields.password}</span>}
          </div>
          {mode === 'register' && (
            <div className="field">
              <label htmlFor="confirm">Confirm password</label>
              <input
                id="confirm"
                className={`input ${fields.confirm ? 'invalid' : ''}`}
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
              {fields.confirm && <span className="field-error">{fields.confirm}</span>}
              <span className="muted" style={{ fontSize: 12 }}>
                At least 6 characters. This account is shared with the other game.
              </span>
            </div>
          )}
          <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
            {busy ? 'Please wait…' : mode === 'login' ? 'Enter the Jianghu' : 'Create Account'}
          </button>
          {mode === 'login' && (
            <p className="muted" style={{ fontSize: 12, marginTop: 14, marginBottom: 0 }}>
              <button
                type="button"
                onClick={() => void forgotPassword()}
                style={{
                  fontSize: 12,
                  padding: 0,
                  background: 'none',
                  border: 'none',
                  color: 'inherit',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                Forgot your password?
              </button>{' '}
              You will get a reset email. This is the same account as the other game.
              {resetNote && (
                <>
                  <br />
                  {resetNote}
                </>
              )}
            </p>
          )}
        </form>
      </div>
    </main>
  );
}
